import { NgFor, NgIf } from '@angular/common';
import { Component, HostListener, Inject, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Brand } from './models/brand';
import { Category } from './models/category';
import { SiteInfo } from './models/blog';
import { Seasonal } from './models/seasonal';
import { BrandApiService } from './services/brand-api.service';
import { CategoryApiService } from './services/category-api.service';
import { SeasonalApiService } from './services/seasonal-api.service';
import { Injector } from '@angular/core';
import { PagesApiService } from './services/pages-api.service';
import { SubscriberModel } from './models/subscriberModel';
import { FormsModule } from '@angular/forms';
import { GeoLocationService } from './services/geo-location.service';

@Component({
  selector: 'app-root',
  imports: [NgIf, NgFor, RouterOutlet, FormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent {
  title = 'DealsZoUI';
  brands: Brand[] = [];
  footerBrands: Brand[] = [];
  topBrands: Brand[] = [];
  categories: Category[] = [];
  footerCategories: Category[] = [];
  currentYear = new Date().getFullYear();
  pendingCalls = 0;
  searchQuery = '';
  searchedBrands: Brand[] = [];
  searchedCategories: Category[] = [];
  seasonalEvents: Seasonal[] = [];
  ongoingSeasonalEevent: Seasonal = {} as Seasonal;
  socialMediaUrls: SiteInfo[] = [];
  showDropdown = false;
  contactMail: string = 'contact@dealszo.com';
  geolocation: any;
  subscriber: SubscriberModel = {} as SubscriberModel;
  subscriberEmail = '';
  subscriptionMessage = '';

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private brandApi: BrandApiService,
    private categoryApi: CategoryApiService,
    private seasonalApi: SeasonalApiService,
    private pageMetaApi: PagesApiService,
    private geoService: GeoLocationService,
    private router: Router,
    private injector: Injector
  ) {
    this.loadAllData();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        const shouldNormalizeUrl =
          url !== '/' &&
          !url.includes('?') &&
          !url.endsWith('/') &&
          !url.match(/\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+/);

        if (shouldNormalizeUrl) {
          this.router.navigateByUrl(`${url}/`, { replaceUrl: true });
        }
      }
    });
  }

  directToHome(): void {
    window.location.href = '/';
  }

  loadAllData(): void {
    this.pendingCalls = 4;

    this.getBrands();
    this.getCategories();
    this.getOnGoingSeasonalEevents();
    this.getSocialMediaUrlsWhenIdle();
  }

  getBrands(): void {
    this.brandApi.getStores(1, 1, 10).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.brands = data.Data;
        this.topBrands = data.Data.slice(0, 6);
        this.footerBrands = data.Data.slice(5, 10);
      }
      this.checkIfAllDone();
    });
  }

  getCategories(): void {
    this.categoryApi.getTopCategories(1).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.categories = data.Data;
        this.footerCategories = data.Data.slice(0, 5);
      }
      this.checkIfAllDone();
    });
  }

  getSocialMediaUrlsWhenIdle(): void {
    const load = () => {
      import('./services/pages-api.service').then(({ PagesApiService }) => {
        this.injector.get(PagesApiService).getSocialMediaUrls().subscribe((result) => {
          if (result.Message === 'Data Fetched Successfully!!!') {
            this.socialMediaUrls = result.Data;
          }
          this.checkIfAllDone();
        });
      });
    };
  }

  getOnGoingSeasonalEevents(): void {
    this.seasonalApi.getOngoingSeasonalEvents().subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.ongoingSeasonalEevent = data.Data.find((s) => s.IsOngoing) as Seasonal;
        this.seasonalEvents = this.ongoingSeasonalEevent
          ? data.Data.filter((s) => s.SeasonalId !== this.ongoingSeasonalEevent.SeasonalId).slice(0, 5)
          : data.Data.slice(0, 5);
      }
      this.checkIfAllDone();
    });
  }

  getSearhedStores(): void {
    this.brandApi.getStoresBySearchQuery(this.searchQuery)
      .subscribe((data) => {
        if (data.Message === 'Data Fetched Successfully!!!') {
          this.searchedBrands = data.Data;
          this.showDropdown = true;
        }
        else if (data.Message === 'No Records Found') {
          this.searchedBrands = [];
        }
      });
  }

  @HostListener('document:click', ['$event'])
  closeSearch(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-container')) {
      this.showDropdown = false;
    }
  }

  getSearchedCategories(): void {
    this.categoryApi.getCategoriesBySearchQuery(this.searchQuery).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.searchedCategories = data.Data;
        this.showDropdown = true;
      } else if (data.Message === 'No Records Found') {
        this.searchedCategories = [];
      }
    });
  }

  normalizeSearchQuery(value: string): string {
    return (value ?? '')
      .replace(/[\\/]+/g, ' ')
      .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  updateSearchQuery(event: Event): void {
    this.searchQuery = this.normalizeSearchQuery((event.target as HTMLInputElement).value);
    this.searchStoresNCategories();
  }

  checkIfAllDone(): void {
    this.pendingCalls -= 1;

    if (this.pendingCalls === 0) {
    }
  }

  directToBrandCouponPage(brand: Brand, isSearch: boolean): void {
    if (brand?.SeoName) {
      window.location.href = `${brand.SeoName}-coupons/`;
    } else {
      console.error('Brand is undefined or invalid:', brand);
    }

    if (isSearch) {
      const closeIcon = document.querySelector('.sidebar-close-icon') as HTMLElement | null;
      closeIcon?.click();
    }
  }

  searchStoresNCategories(): void {
    if (!this.searchQuery?.trim()) {
      this.searchedBrands = [];
      this.searchedCategories = [];
      return;
    }

    if (this.searchQuery.length > 3) {
      this.searchQuery = this.searchQuery.trim();
      this.getSearhedStores();
      this.getSearchedCategories();
    } else {
      this.searchedBrands = [];
      this.searchedCategories = [];
    }
  }

  getSocialMediaUrl(name: string): string {
    return this.socialMediaUrls.find(
      x => x.PageName?.toLowerCase() === name.toLowerCase()
    )?.PageContent || '/';
  }

  isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  onSubscribe(): void {
    if (!this.isValidEmail(this.subscriberEmail)) {
      this.subscriptionMessage = 'Please enter a valid email address.';
      return;
    }

    this.geoService.getGeolocation().subscribe((data) => {
      this.geolocation = data;

      if (!data.success) {
        return;
      }

      this.subscriber.SubscriberIp = data.ip;
      this.subscriber.SubscriberCountry = data.country;
      this.subscriber.SubscriberCity = data.city;
      this.subscriber.SubscriberRegion = data.region;
      this.subscriber.SubscriberCode = data.postal;
      this.subscriber.SubscriberEmail = this.subscriberEmail;

      if (this.subscriberEmail.trim()) {
        this.pageMetaApi.postSubscriber(this.subscriber).subscribe((response) => {
          if (response.Message === 'Subscriber already existed with this email !!!') {
            this.subscriptionMessage = 'Email Already Registered';
          } else if (response.Message === 'Subscriber Added Successfully!!!') {
            this.subscriptionMessage = 'Successfully Subscribed';
          } else {
            alert(response.Message);
          }
        });
      } else {
        this.subscriptionMessage = 'Please Enter Proper Email';
      }
    });
  }

  onSearchEnter(): void {
    const query = this.normalizeSearchQuery(this.searchQuery);
    if (!query || query.length < 4) {
      return;
    }
    this.searchQuery = query;
    this.showDropdown = false;
    this.router.navigateByUrl(`/search/?q=${encodeURIComponent(query)}`);
  }
}
